/**
 * Propriétés pour les taches de type service externe
 * Cela va être enregistré dans le BPMN sous la forme :
 * <bpmn:serviceTask id="a" name="mail" 
 *  aktor:action="webhook"
 *  aktor:label="{\"fr\": \"Webhook\", \"en\": \"Webhook\"}">
 * 
 * aktor:action : l'identifiant de l'action (par exemple 'webhook')
 * aktor:label : le label multilingue de l'action au format JSON
 * aktor:type : le type de service
 * aktor:params-json : les paramètres de l'action au format JSON
 */
import { html } from 'htm/preact';
import { SelectEntry, isSelectEntryEdited, TextFieldEntry, TextAreaEntry, CheckboxEntry } from '@bpmn-io/properties-panel';
import { useService } from 'bpmn-js-properties-panel';
import EventBus from '@services/EventBus';

// Écouter l'événement de mise à jour des services disponibles
let availableServices = [];
let originalServices = [];
EventBus.on('updateServices', (services) => {
  availableServices = services;
});
EventBus.on('updateOriginalServices', (services) => {
  originalServices = services;
});

function ExternalServiceProps(element) {
  let fields = [{
    id: 'action',
    element,
    component: ActionField,
    isEdited: isSelectEntryEdited
  }];

  const action = element.businessObject['action'] || '';
  const actionDetails = availableServices[action];
  
  if (actionDetails && actionDetails.helperText) {
    fields.push({
      id: 'helperText',
      element,
      component: HelperTextField,
      isEdited: () => true
    });
  }

  if (actionDetails && actionDetails.params) {
    fields.push({
      id: 'params',
      element,
      component: ParamsField,
      isEdited: () => true
    });
  }

  return fields;
}

function ActionField({ element, id }) {
  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return element.businessObject['action'] || '';
  };

  const setValue = (value) => {
    const properties = {
      'action': value,
      'type': availableServices[value].type,
      'params-json': '{}'
    };
    
    // Ajouter le label multilingue depuis les services originaux
    if (originalServices[value] && originalServices[value].label) {
      properties['label'] = JSON.stringify(originalServices[value].label);
    }
    
    return modeling.updateProperties(element, properties);
  };

  const getOptions = () => {
    return Object.keys(availableServices).map(service => ({
      value: service,
      label: availableServices[service].label
    }));
  };

  return html`<${SelectEntry}
    id=${id}
    element=${element}
    label=${translate('Action')}
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />`;
}

function ParamsField({ element, id }) {
  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getParams = () => {
    try {
      return JSON.parse(element.businessObject['params-json'] || '{}');
    } catch (e) {
      return {};
    }
  };

  const setParams = (params) => {
    return modeling.updateProperties(element, {
      'params-json': JSON.stringify(params)
    });
  };

  const action = element.businessObject['action'] || '';
  const actionDetails = availableServices[action];
  
  if (!actionDetails || !actionDetails.params) {
    return null;
  }

  const params = getParams();
  const hasRequiredParam = (actionDetails.params || []).some(p => p.required);

  const handleParamChange = (name, value) => {
    const newParams = { ...params, [name]: value };
    setParams(newParams);
  };

  const getParamValue = (param) => {
    const currentValue = params[param.name];
    if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
      return currentValue;
    }
    return param.defaultValue !== undefined ? param.defaultValue : (param.type === 'checkbox' ? false : (param.type === 'multiselect' ? [] : ''));
  };

  return html`
    <div class="params-container">
      ${actionDetails.params.map(param => {
        const label = param.label + (param.required ? ' *' : '');
        let tooltip = param.helperText || '';
        switch (param.type) {

          case 'text':
            return html`<${TextFieldEntry}
              id=${`${id}-${param.name}`}
              element=${element}
              label=${label}
              getValue=${() => getParamValue(param)}
              setValue=${(value) => handleParamChange(param.name, value)}
              debounce=${debounce}
              tooltip=${tooltip}
            />`;

          case 'textarea':
            return html`<${TextAreaEntry}
              id=${`${id}-${param.name}`}
              element=${element}
              label=${label}
              getValue=${() => getParamValue(param)}
              setValue=${(value) => handleParamChange(param.name, value)}
              debounce=${debounce}
              tooltip=${tooltip}
            />`;

          case 'checkbox':
            return html`<${CheckboxEntry}
              id=${`${id}-${param.name}`}
              element=${element}
              label=${label}
              getValue=${() => getParamValue(param)}
              setValue=${(value) => handleParamChange(param.name, value)}
              tooltip=${tooltip}
            />`;

          case 'select':
            return html`<${SelectEntry}
              id=${`${id}-${param.name}`}
              element=${element}
              label=${label}
              getValue=${() => getParamValue(param)}
              setValue=${(value) => handleParamChange(param.name, value)}
              getOptions=${() => {
                const options = Object.entries(param.enum).map(([value, label]) => ({ value, label }));
                if (!param.required) {
                  options.unshift({ value: '', label: '- ' + translate('No selection') + ' -' });
                }
                return options;
              }}
              debounce=${debounce}
              tooltip=${tooltip}
            />`;

          case 'multiselect':
            return html`
              <div class="multiselect-container bio-properties-panel-entry">
                <label class="bio-properties-panel-label">
                  ${tooltip ? html`<div tabindex="0" class="bio-properties-panel-tooltip-wrapper" title=${tooltip}>${label}</div>` : label}
                </label>
                ${Object.entries(param.enum).map(([value, label]) => {
                  const currentValues = getParamValue(param);
                  return html`
                    <div class="checkbox-item">
                      <${CheckboxEntry}
                        id=${`${id}-${param.name}-${value}`}
                        element=${element}
                        label=${label}
                        getValue=${() => currentValues.includes(value)}
                        setValue=${(checked) => {
                          const newValues = checked
                            ? [...currentValues, value]
                            : currentValues.filter(v => v !== value);
                          handleParamChange(param.name, newValues);
                        }}
                      />
                    </div>
                  `;
                })}
              </div>
            `;

          default:
            return null;
        }
      })}
      ${hasRequiredParam ? html`
        <div class="bio-properties-panel-entry">
          <div class="bio-properties-panel-label" style="font-style: italic; margin-top: 20px;">* ${translate('Required field')}</div>
        </div>
      ` : null}
    </div>
  `;
}

function HelperTextField({ element, id }) {
  
  const action = element.businessObject['action'] || '';
  const actionDetails = availableServices[action];
  
  if (!actionDetails || !actionDetails.helperText) {
    return null;
  }

  return html`<div class="bio-properties-panel-entry">
    <div class="bio-properties-panel-label">
      ${actionDetails.helperText}
    </div>
  </div>`;
}


export default ExternalServiceProps;